import { useContext, useMemo } from 'react';
import { type BaseTag } from '@hungryroot/rest';
import { type TagGroups } from '@hungryroot/tags';
import {
  useApiV1TagGroup as useTagGroup,
  useApiV1TagGroupShuffled as useTagGroupShuffled,
} from '@hungryroot/tags';
import { QuizContext } from '../../quiz-context';
import { type QuizTagKeys, type QuizTypes } from '../../quiz-hooks';
import { type QuizPreferenceItem } from '../../segment/quizAnalytics';
import {
  type OnItemSelection,
  getSelectedKeys,
  renderTags,
  type ControlChildren,
} from '../control-utils';
import { ControlSelect } from '../ControlSelect/ControlSelect';

type Props<K extends QuizTagKeys> = {
  ariaLabel: string;
  question: K;
  tagGroupId: TagGroups;
  expandDislikes?: boolean;
  children: ControlChildren;
  layoutVariant?:
    | 'one-column'
    | 'two-column'
    | 'one-column-to-two-column'
    | 'three-column';
  max?: number;
  staticList?: boolean;
  defaultValue: [] | undefined;
  onItemSelectionChange?: OnItemSelection[];
  onTrackSelected?: (items: QuizPreferenceItem[]) => void;
} & ( // Either filterIds or filter can be passed, but not both, and neither is required
  | { filterIds?: number[]; filter?: never }
  | { filter?: (tag: BaseTag) => boolean; filterIds?: never }
  | Record<string, never>
);

/**
 * For persistence and active states, see `use-quiz-loader.ts`
 */
export function ControlTagSelect<K extends QuizTagKeys>(props: Props<K>) {
  const {
    ariaLabel,
    max,
    question,
    tagGroupId,
    expandDislikes,
    children,
    layoutVariant,
    staticList,
    defaultValue,
    onItemSelectionChange,
    onTrackSelected,
  } = props;

  const { data: staticTagGroupData } = useTagGroup(
    { tagGroupId, expandDislikes },
    {
      enabled: !!staticList,
    }
  );
  const { data: shuffledTagGroupData } = useTagGroupShuffled(
    { tagGroupId, expandDislikes },
    {
      enabled: !staticList,
    }
  );

  const tagGroupData = staticTagGroupData ?? shuffledTagGroupData;

  const { quiz } = useContext(QuizContext);

  const filterIds = useMemo(() => {
    if ('filter' in props && props.filter) {
      return tagGroupData?.tags[0].filter(props.filter).map((tag) => tag.id);
    }

    if ('filterIds' in props && props.filterIds) {
      return props.filterIds;
    }

    return undefined;
  }, [props, tagGroupData?.tags]);

  if (tagGroupData === undefined) {
    return null;
  }

  const items = tagGroupData.tags[0].map((tag) => ({
    label: tag.name,
    value: tag.id,
  }));

  const selectedKeys = getSelectedKeys(quiz, question);

  return (
    <ControlSelect
      ariaLabel={ariaLabel}
      question={question}
      defaultValue={defaultValue}
      layoutVariant={layoutVariant}
      max={max}
      items={items}
      selectedKeys={selectedKeys as QuizTypes[]}
      onItemSelectionChange={onItemSelectionChange}
      onTrackSelected={onTrackSelected}
    >
      {renderTags(question, quiz, tagGroupData.tags[0], children, {
        filterIds,
      })}
    </ControlSelect>
  );
}
